let realtimeTopicCounter = 0;

/**
 * Supabase reuses channel instances that have the same topic. Reusing a topic
 * across multiple React components is dangerous because realtime-js throws if
 * one component tries to add `.on()` callbacks after another has subscribed.
 *
 * Always use this helper for component-level realtime subscriptions so every
 * hook instance owns an isolated channel and can clean it up safely.
 */
export function createUniqueRealtimeTopic(prefix: string, scope: string): string {
  realtimeTopicCounter = (realtimeTopicCounter + 1) % Number.MAX_SAFE_INTEGER;
  const safePrefix = prefix.replace(/[^a-zA-Z0-9_-]/g, "-");
  const safeScope = scope.replace(/[^a-zA-Z0-9_-]/g, "-");
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${safePrefix}-${safeScope}-${Date.now()}-${realtimeTopicCounter}-${randomPart}`;
}

/**
 * Security rule for live updates.
 *
 * Every live channel in this app must be a plain database-change listener,
 * which is filtered server side by the same access rules as a normal query,
 * so one nursery can never see another nursery's data.
 *
 * Message-passing channels (broadcast / presence, and anything marked
 * `private`) are deliberately NOT used: they would need their own per-topic
 * access rules on top of the database ones. Use this options object when
 * opening a channel so that stays true, and never pass `private: true`.
 */
export const SAFE_REALTIME_CHANNEL_OPTIONS = {
  config: {
    broadcast: { self: false, ack: false },
    presence: { key: "" },
    private: false as const,
  },
} as const;

export function isRealtimeCallbackOrderError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.includes("cannot add") && message.includes("after 'subscribe()'");
}
